import React from 'react';
import { Brain, Users, Home, ExternalLink, CheckCircle, Briefcase } from 'lucide-react';
import { professionalData } from '../professionalData';

const ServicesPage = () => {
  return (
    <div>
      {/* HEADER SECTION */}
      <section className="section" style={{ paddingTop: '6rem', paddingBottom: '4rem' }} data-testid="services-hero">
        <div className="container">
          <div style={{ textAlign: 'center', maxWidth: '900px', margin: '0 auto' }}>
            <p className="uppercase-small" style={{ marginBottom: '1rem' }}>
              CLINICAL SERVICES
            </p>
            <h1 style={{ marginBottom: '1.5rem' }}>
              Core Pillars of Practice
            </h1>
            <p style={{ 
              fontSize: '1.25rem',
              lineHeight: 1.8,
              color: 'var(--text-muted)',
              fontWeight: 500
            }}>
              Engineering high-resolution diagnostic mapping and evidence-based interventions for complex neurodivergent presentations.
            </p>
          </div>
        </div>
      </section>

      {/* TIER 1: THE B2C PILLARS - 3-Column Grid */}
      <section className="section-alt" data-testid="b2c-pillars">
        <div className="container">
          <div className="grid-3" style={{ gap: '2rem', alignItems: 'stretch' }}>
            
            {/* Card 1: Psycho-Diagnostics */}
            <div className="glass-card" style={{ 
              display: 'flex', 
              flexDirection: 'column',
              padding: '2.5rem',
              textAlign: 'center'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                margin: '0 auto 1.5rem',
                background: 'linear-gradient(135deg, var(--accent) 0%, #D4A574 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Brain size={36} color="white" />
              </div>
              
              <h3 style={{ 
                fontSize: '1.5rem',
                fontFamily: 'var(--font-heading)',
                fontWeight: 600,
                marginBottom: '1rem',
                color: 'var(--primary)'
              }}>
                Psycho-Diagnostics
              </h3>
              
              <p style={{
                fontSize: '1rem',
                lineHeight: 1.8,
                color: 'var(--text-main)',
                flex: 1
              }}>
                Precision mapping of neuro-developmental disorders including ASD and ADHD. I specialize in gold-standard objective instrumentation (<strong>ADOS-2</strong>, <strong>WISC-IV/V</strong>, <strong>ISAA</strong>, <strong>Com-DEALL</strong>) to bypass parent-reporting bias and establish a definitive clinical baseline.
              </p>
            </div>

            {/* Card 2: Behavioral Engineering */}
            <div className="glass-card" style={{ 
              display: 'flex', 
              flexDirection: 'column',
              padding: '2.5rem',
              textAlign: 'center'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                margin: '0 auto 1.5rem',
                background: 'linear-gradient(135deg, #1E3A8A 0%, #3B5BA5 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Users size={36} color="white" />
              </div>
              
              <h3 style={{ 
                fontSize: '1.5rem',
                fontFamily: 'var(--font-heading)',
                fontWeight: 600,
                marginBottom: '1rem',
                color: 'var(--primary)'
              }}>
                Behavioral Engineering
              </h3>
              
              <p style={{
                fontSize: '1rem',
                lineHeight: 1.8,
                color: 'var(--text-main)',
                flex: 1
              }}>
                Utilizing <strong>ABA-integrated protocols</strong> and <strong>Positive Behaviour Support (PBS)</strong> to remediate deficits in social reciprocity and emotional regulation. We move beyond basic compliance to build functional, sustainable life skills.
              </p>
            </div>

            {/* Card 3: Systemic Restructuring */}
            <div className="glass-card" style={{ 
              display: 'flex', 
              flexDirection: 'column',
              padding: '2.5rem',
              textAlign: 'center'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                margin: '0 auto 1.5rem',
                background: 'linear-gradient(135deg, #8F9C82 0%, #A8B89E 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Home size={36} color="white" />
              </div>
              
              <h3 style={{ 
                fontSize: '1.5rem',
                fontFamily: 'var(--font-heading)',
                fontWeight: 600,
                marginBottom: '1rem',
                color: 'var(--primary)'
              }}>
                Systemic Restructuring
              </h3>
              
              <p style={{
                fontSize: '1rem',
                lineHeight: 1.8,
                color: 'var(--text-main)',
                flex: 1
              }}>
                Child psychology cannot exist in a vacuum. I focus heavily on caregiver psychoeducation—mapping intergenerational family patterns to modify parenting styles, resolve trauma, and mitigate the psychosocial stressors impacting the child&apos;s development.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TIER 2: MODERN MINDS HQ */}
      <section className="section" data-testid="modern-minds-hq">
        <div className="container">
          <div className="glass-card-strong" style={{ 
            maxWidth: '1000px',
            margin: '0 auto',
            padding: '3rem',
            textAlign: 'center'
          }}>
            <h2 style={{ 
              fontSize: '2rem',
              marginBottom: '1rem',
              color: 'var(--primary)',
              fontFamily: 'var(--font-heading)'
            }}>
              Modern Minds
            </h2>
            
            <p style={{
              fontSize: '1.125rem',
              fontWeight: 500,
              color: 'var(--accent)',
              marginBottom: '2rem'
            }}>
              My primary clinical headquarters, operating on a sovereign &apos;Closed-Loop Intervention&apos; model in Lucknow, India.
            </p>

            {/* Feature Bullets as Small Frosted Cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              {[
                'High-resolution psychometric mapping',
                'ABA/PBS behavioral frameworks',
                'Neuro-affirming home environment design'
              ].map((feature, index) => (
                <div 
                  key={index}
                  style={{
                    background: 'rgba(255, 255, 255, 0.5)',
                    backdropFilter: 'blur(10px)',
                    padding: '1rem 1.5rem',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    fontSize: '0.95rem',
                    color: 'var(--text-main)',
                    textAlign: 'left'
                  }}
                >
                  <CheckCircle size={18} color="var(--accent)" style={{ flexShrink: 0 }} />
                  {feature}
                </div>
              ))}
            </div>

            <a
              href={professionalData.clinic.website}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '1.125rem',
                padding: '1rem 2rem'
              }}
              data-testid="modern-minds-website"
            >
              Visit Modern Minds Clinic
              <ExternalLink size={20} />
            </a>
          </div>
        </div>
      </section>

      {/* TIER 3: CLINICAL PARTNERSHIPS (B2B) */}
      <section className="section-alt" data-testid="b2b-partnerships">
        <div className="container">
          <div className="glass-card" style={{ 
            maxWidth: '1000px',
            margin: '0 auto',
            padding: '3rem'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              margin: '0 auto 2rem',
              background: 'linear-gradient(135deg, #1E3A8A 0%, #3B5BA5 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Briefcase size={36} color="white" />
            </div>

            <h2 style={{ 
              fontSize: '2rem',
              marginBottom: '0.5rem',
              color: 'var(--primary)',
              fontFamily: 'var(--font-heading)',
              textAlign: 'center'
            }}>
              B2B Diagnostic Architecture & Professional Referrals
            </h2>
            
            <p style={{
              fontSize: '1.125rem',
              fontWeight: 500,
              color: 'var(--accent)',
              marginBottom: '2rem',
              textAlign: 'center'
            }}>
              Outsourced psychometric evaluations for medical institutions, pediatricians, and educational frameworks.
            </p>

            <p style={{
              fontSize: '1rem',
              lineHeight: 1.8,
              color: 'var(--text-main)',
              marginBottom: '2rem',
              textAlign: 'center',
              maxWidth: '800px',
              margin: '0 auto 2rem'
            }}>
              I partner with external clinics and educational gatekeepers to provide high-fidelity, independent psychometric evaluations. By deploying a gold-standard diagnostic stack (<strong>ADOS-2</strong>, <strong>WISC-IV</strong>, <strong>ISAA</strong>, <strong>Rorschach Exner</strong>), I deliver structured, data-driven Clinical Scorecards that empower your transdisciplinary teams to make definitive, evidence-based management decisions.
            </p>

            <div style={{ textAlign: 'center' }}>
              <a
                href="/contact"
                className="btn-primary"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '1.125rem',
                  padding: '1rem 2rem',
                  background: 'var(--primary)',
                  border: 'none'
                }}
                data-testid="b2b-inquiry-btn"
              >
                <Briefcase size={20} />
                Inquire About Clinical Partnerships
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
